import type { DashboardSnapshot } from '@/data/types'
import type { Category, MilestoneDefinition } from './types'
import { metricValue, isMilestoneReached } from './detector'

export interface MilestoneProgress {
  def: MilestoneDefinition
  current: number
  target: number
  pct: number
}

export function nearestPerCategory(
  defs: MilestoneDefinition[],
  snapshot: DashboardSnapshot,
  unlocked: ReadonlySet<string>,
): Partial<Record<Category, MilestoneProgress>> {
  const out: Partial<Record<Category, MilestoneProgress>> = {}
  for (const def of defs) {
    if (unlocked.has(def.id)) continue
    if (isMilestoneReached(def, snapshot)) continue
    const current = metricValue(snapshot, def.metric)
    const pct = computePct(def, current)
    const best = out[def.category]
    if (!best || pct > best.pct) {
      out[def.category] = { def, current, target: def.threshold, pct }
    }
  }
  return out
}

export function nearestOverall(
  defs: MilestoneDefinition[],
  snapshot: DashboardSnapshot,
  unlocked: ReadonlySet<string>,
  count = 4,
): MilestoneProgress[] {
  const candidates: MilestoneProgress[] = []
  for (const def of defs) {
    if (unlocked.has(def.id)) continue
    if (isMilestoneReached(def, snapshot)) continue
    const current = metricValue(snapshot, def.metric)
    candidates.push({ def, current, target: def.threshold, pct: computePct(def, current) })
  }
  candidates.sort((a, b) => b.pct - a.pct)
  return candidates.slice(0, count)
}

function computePct(def: MilestoneDefinition, current: number): number {
  if (def.direction === 'gte') {
    return Math.min(1, current / Math.max(1, def.threshold))
  }
  const baseline = Math.max(def.threshold * 2, def.threshold + 1)
  return Math.min(1, Math.max(0, (baseline - current) / (baseline - def.threshold)))
}
